import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const blank = { name: "", email: "", password: "", role: "finance_agent", customer_id: "", phone: "" };
const unwrap = (data) => Array.isArray(data) ? data : (data?.items || data?.users || data?.customers || []);

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(unwrap(data));
  }, []);
  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/users"), api.get("/customers")])
      .then(([u,c]) => { if(mounted) { setUsers(unwrap(u.data)); setCustomers(unwrap(c.data)); } })
      .catch(e => toast.error(formatApiError(e.response?.data?.detail) || "Chargement impossible"))
      .finally(() => { if(mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);
  const update = (key, value) => setForm(f => ({...f, [key]:value}));
  const create = async (e) => {
    e.preventDefault();
    if(saving) return;
    if(form.role === "school_admin" && !form.customer_id) return toast.error("Choisissez un client");
    setSaving(true);
    try {
      await api.post("/users", {
        name:form.name.trim(), email:form.email.trim(), password:form.password, role:form.role,
        phone:form.phone.trim() || null,
        customer_id:form.role === "school_admin" ? form.customer_id : null
      });
      await reload();
      setForm(blank);
      toast.success("Compte créé. Mot de passe à changer à la première connexion.");
    } catch(e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Création impossible");
    } finally { setSaving(false); }
  };
  const toggle = async (u) => {
    if(u.role === "principal_admin" || busy) return;
    const active = u.active === false;
    if(!window.confirm(`Confirmer ${active ? "la réactivation" : "la désactivation"} de ${u.email} ?`)) return;
    setBusy(u.id);
    try {
      await api.patch(`/users/${encodeURIComponent(u.id)}/status`, {active});
      await reload();
      toast.success("Statut actualisé");
    } catch(e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Modification impossible");
    } finally { setBusy(null); }
  };
  return <div className="space-y-6">
    <PageHeader title="Gestion des utilisateurs" subtitle="Comptes financiers et administrateurs scolaires" />
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Créer un compte</h2>
      <form onSubmit={create} className="grid gap-4 md:grid-cols-2">
        <div><Label htmlFor="u-name">Nom complet</Label><Input id="u-name" required minLength={2} maxLength={150} value={form.name} onChange={e=>update("name",e.target.value)} /></div>
        <div><Label htmlFor="u-email">Email</Label><Input id="u-email" type="email" required value={form.email} onChange={e=>update("email",e.target.value)} /></div>
        <div><Label htmlFor="u-role">Rôle</Label><select id="u-role" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value,customer_id:""}))} className="w-full h-10 border rounded-md px-3 text-sm">
          <option value="finance_agent">Agent financier</option><option value="school_admin">Administrateur scolaire</option>
        </select></div>
        {form.role === "school_admin" && <div><Label htmlFor="u-customer">Client de rattachement</Label><select id="u-customer" required value={form.customer_id} onChange={e=>update("customer_id",e.target.value)} className="w-full h-10 border rounded-md px-3 text-sm">
          <option value="">Sélectionner un client</option>{customers.map(c=><option key={c.id} value={c.id}>{c.name || c.company_name || c.id}</option>)}
        </select></div>}
        <div><Label htmlFor="u-phone">Téléphone (facultatif)</Label><Input id="u-phone" value={form.phone} onChange={e=>update("phone",e.target.value)} /></div>
        <div><Label htmlFor="u-password">Mot de passe temporaire (12 caractères minimum, 72 octets UTF-8 maximum)</Label><Input id="u-password" type="password" required minLength={12} autoComplete="new-password" value={form.password} onChange={e=>update("password",e.target.value)} /></div>
        <div className="md:col-span-2"><p className="text-sm text-slate-500 mb-3">Transmettez le mot de passe temporaire par un canal sûr. Il sera changé à la première connexion.</p><Button type="submit" disabled={saving}>{saving ? "Création…" : "Créer le compte"}</Button></div>
      </form>
    </Card>
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Utilisateurs existants</h2>
      {loading ? <p>Chargement…</p> : <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-left border-b"><th className="p-2">Nom</th><th className="p-2">Email</th><th className="p-2">Rôle</th><th className="p-2">Statut</th><th className="p-2">Action</th></tr></thead>
        <tbody>{users.map(u=><tr key={u.id} className="border-b">
          <td className="p-2">{u.name || "—"}</td><td className="p-2">{u.email}</td><td className="p-2">{u.role}</td>
          <td className="p-2">{u.active === false ? "Désactivé" : "Actif"}{u.must_change_password ? " · Changement requis" : ""}</td>
          <td className="p-2">{u.role !== "principal_admin" && <Button type="button" variant="outline" size="sm" disabled={busy !== null} onClick={()=>toggle(u)}>{busy === u.id ? "En cours…" : u.active === false ? "Réactiver" : "Désactiver"}</Button>}</td>
        </tr>)}</tbody>
      </table>{users.length === 0 && <p className="p-3">Aucun compte trouvé</p>}</div>}
    </Card>
  </div>;
}
